import { X, Search, Package2, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

import ArrowTooltip from '@/components/custom-ui/customTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

type Props = {
  items: { label: string; value: number; isImportant: boolean }[];
  selectedItems: number[];
  setSelectedItems: React.Dispatch<React.SetStateAction<number[]>>;
};

const ItemSelection = ({ items, selectedItems, setSelectedItems }: Props) => {
  const [searchText, setSearchText] = useState('');
  const [Important, setImportant] = useState(false);

  const toggleSelection = (id: number) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // تصفية العناصر بناءً على نص البحث
  const filteredItems = items.filter(({ label, isImportant }) => {
    const search = searchText.trim().toLowerCase();
    return label.toLowerCase().includes(search) && (!Important || isImportant);
  });

  const toggleImportant = () => {
    setImportant((prev) => !prev);
  };

  function getImportantItems() {
    const importantItems = items.filter((item) => item.isImportant);
    setSelectedItems(importantItems.map((item) => item.value));
  }

  return (
    <div className="space-y-3">
      {/* Search Box with Important Filter */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
        <Input
          type="text"
          placeholder="ابحث عن صنف..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full pl-10 pr-12 h-9 text-sm bg-slate-50 border-slate-200 focus:bg-white dark:bg-slate-800 dark:border-slate-700"
        />
        <Button
          variant={Important ? 'default' : 'ghost'}
          size="icon"
          onClick={toggleImportant}
          className={cn(
            "absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2",
            Important && "bg-amber-500 hover:bg-amber-600 text-white"
          )}
        >
          <AlertTriangle className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {filteredItems.length} من {items.length} صنف
          </span>
          {items.filter((item) => item.isImportant).length > 0 && (
            <Button
              onClick={getImportantItems}
              variant="outline"
              size="sm"
              className="h-6 gap-1 px-2 text-xs"
            >
              <AlertTriangle className="h-3 w-3 text-amber-500" />
              مهمة ({items.filter((item) => item.isImportant).length})
            </Button>
          )}
        </div>
        {selectedItems.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedItems([])}
            className="h-6 gap-1 px-2 text-xs text-slate-500 hover:text-red-600 dark:text-slate-400 dark:hover:text-red-400"
          >
            <X className="h-3 w-3" />
            مسح ({selectedItems.length})
          </Button>
        )}
      </div>

      {/* Items List */}
      <ScrollArea className="h-52">
        <div className="space-y-1 pr-3">
          {filteredItems.length > 0 ? (
            filteredItems.map(({ label, value, isImportant }) => {
              const isSelected = selectedItems.includes(value);
              return (
                <div
                  key={value}
                  className={cn(
                    "group flex items-center gap-3 rounded-md p-2.5 transition-colors cursor-pointer w-full",
                    isSelected 
                      ? "bg-emerald-50 border border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800/50"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  )}
                  onClick={() => toggleSelection(value)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelection(value)}
                    className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                  />
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-w-full">
                    <Package2 className={cn(
                      "h-4 w-4 flex-shrink-0",
                      isSelected ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400"
                    )} />
                    <span className={cn(
                      "text-sm truncate",
                      isSelected ? "font-medium text-emerald-900 dark:text-emerald-100" : "text-slate-700 dark:text-slate-300"
                    )}>
                      {label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isImportant && (
                      <ArrowTooltip color="primary" msg="صنف مهم">
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 border border-amber-200 dark:bg-amber-900/30 dark:border-amber-800/50">
                          <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                        </div>
                      </ArrowTooltip>
                    )}
                    {isSelected && (
                      <Badge variant="secondary" className="h-5 px-2 text-xs">
                        محدد
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center w-full">
              <Package2 className="h-8 w-8 text-slate-300 dark:text-slate-600 mb-2" />
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">
                لا توجد نتائج
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {Important ? "لا توجد أصناف مهمة" : "جرب تغيير مصطلح البحث"}
              </p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ItemSelection;
